import { FC, FormEvent, Fragment, MouseEventHandler, useState } from 'react';
import {
  BKJButton,
  Flex,
  BKJLoadingSpinner,
  BKJPillDropdown,
  BKJThemeColorType,
  BKJBadge,
  BKJIconTooltip,
} from '@bookjane2/bookjane-design-library';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import {
  FetchJaneByIdByIdDataProvider,
  useFetchJaneByIdDataContext,
} from 'components/JaneModal/FetchJaneById.data.context';
import { SelectInputPositionByDepartment } from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment';
import { useTranslation } from 'hooks/useTranslation';
import {
  removePositionModalRoutingService,
  useRemovePositionModalUIContext,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/RemovePositionModal.ui.context';
import { Jane } from 'types/graphql';
import { useSelector } from 'react-redux';
import {
  isMultiLocationCallOutFeatureEnabledSelector,
  isMultiLocationSchedulingFeatureEnabledSelector,
  isMultiLocationTieringFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import JaneMenu from 'components/JaneModal/JaneMenu';
import {
  JaneName,
  Text,
  H4,
  ValueText,
  LabelText,
  JaneAvatarWrapper,
  ColorDot,
  LoadingWrapper,
  PendingSignUpText,
  NonAppUserWrapper,
  EllipsisText,
  FieldWrapper,
  HeadSection,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/views/JaneModal/JaneDetailsView/GeneralTab/GeneralTab.styled';
import { TeamMemberJaneAvatar } from 'components/TeamMemberJaneAvatar';
import { FeatureGuard } from 'guards/FeatureGuard';
import i18next from 'i18next';
import { startCase } from 'lodash';
import { TooltipWrapper } from 'components/JaneModal/views/EditJaneView/EditJaneView.styled';
import { SessionService } from 'services/SessionService';
import {
  secondaryLocationCallOutFilter,
  secondaryLocationSchedulingFilter,
  shiftModalGeneralTabValueTextMarginLeft,
} from 'components/JaneModal/views/JaneDetailsView/GeneralTab/GeneralTab.constants';
import { StarRating } from 'components/StarRating';
import { BKJCenteredDialog } from 'components/BKJCenteredDialog';
import isUserAgencySelector from 'store/selectors/userSelectors/isUserAgencySelector';
import { SelectInputPositionByDepartmentProvider } from 'components/JaneModal/views/EditJaneView/SelectInputPositionByDepartment/SelectInputPositionByDepartment.context';
import {
  EditJaneViewFormProvider,
  useEditJaneViewFormContext,
} from 'pages/LocationSettingsPage/PositionsAllocation/RemovePositionModal/views/JaneModal/EditJaneView/EditJaneView.form.context';
import { useURLParameters } from 'hooks';

const Component: FC = () => {
  const {
    values,
    fieldErrors,
    isDisabled,
    isSubmitting,
    onSubmit,
    secondaryPositionsDropdownItems,
    handlePrimaryPosition,
    isDiscardConfirmationOpen,
    setIsDiscardConfirmationOpen,
    handleRemovedSecondaryPosition,
    handleSecondaryPosition,
    numberOfPositionsDeleted,
    showDeactivateConfirmModal,
    setShowDeactivateConfirmModal,
  } = useEditJaneViewFormContext();

  const { t } = useTranslation();
  const {
    data,
    dotColor,
    deactivateTeamMember,
    reactivateTeamMember,
  }: {
    data: Jane;
    dotColor: BKJThemeColorType;
    deactivateTeamMember: () => void;
    reactivateTeamMember: () => void;
    resendInviteTeamMember: () => void;
    showPendingSignUpModal: boolean;
    setShowPendingSignUpModal: (showPendingSignUpModal: boolean) => void;
  } = useFetchJaneByIdDataContext();
  const { locationId } = useRemovePositionModalUIContext();

  const isMultiLocationSchedulingEnabled = useSelector(
    isMultiLocationSchedulingFeatureEnabledSelector(Number(locationId)),
  );

  const isMultiLocationTieringEnabled = useSelector(
    isMultiLocationTieringFeatureEnabledSelector(Number(locationId)),
  );

  const isMultiLocationCallOutEnabled = useSelector(
    isMultiLocationCallOutFeatureEnabledSelector(Number(locationId)),
  );

  const isUserAgency = useSelector(isUserAgencySelector);
  const {
    avatarUrls = { origin: '' },
    accessStatus = { name: '' },
    primaryPosition = { details: { name: '' } },
    customerReviews = { averageRating: '', count: '' },
    communityName = '',
    primaryCommunity = { id: '' },
    usersCommunities = [],
    shorthandName = '',
    pendingSignup = false,
    isMobile = false,
    id: janeId,
  } = data;
  const primaryCommunityId = primaryCommunity?.id;
  const averageRating = !customerReviews?.count ? 0 : Number(customerReviews?.averageRating) || 0;

  const isJanePrimaryLocationSame = Number(locationId) === primaryCommunityId;
  const showEdit = isJanePrimaryLocationSame || SessionService.assertUserType('Agency');
  const [showBlockTeamMemberDialog, setShowBlockTeamMemberDialog] = useState(false);

  return (
    <Fragment>
      <Flex
        flexDirection="column"
        justifyContent="flex-start"
        alignItems="flex-start"
        width="100%"
        borderTop="1px solid rgba(139, 150, 163, 0.33)"
      >
        {showDeactivateConfirmModal && (
          <BKJCenteredDialog
            isOpen={showDeactivateConfirmModal}
            variant="Danger"
            title={
              numberOfPositionsDeleted > 1
                ? t('team_member:Remove_Positions')
                : t('team_member:Remove_Position')
            }
            text={
              numberOfPositionsDeleted > 1
                ? t('team_member:Remove Position Details')
                : t('team_member:Remove Position Detail')
            }
            cancelLabel={t('auth:CANCEL')}
            actionLabel={t('team_member:Remove')}
            onAction={() => {
              handleRemovedSecondaryPosition({} as FormEvent<HTMLFormElement>);
            }}
            onCancel={() => setShowDeactivateConfirmModal(false)}
            onClose={() => setShowDeactivateConfirmModal(false)}
          />
        )}
        {showBlockTeamMemberDialog && (
          <BKJCenteredDialog
            isOpen={showBlockTeamMemberDialog}
            variant="Danger"
            title={t('team_member:DEACTIVATE_TEAM_MEMBER')}
            text={
              isUserAgency
                ? t('team_member:THIS_WILL_BLOCK_THE_TEAM_MEMBER_FROM_ACCESSING')
                : t('team_member:BEFORE_DEACTIVATING_THE_TEAM_MEMBER')
            }
            cancelLabel={i18next.t('auth:CANCEL')}
            actionLabel={i18next.t('team_member:DEACTIVATE')}
            onAction={deactivateTeamMember}
            onCancel={() => setShowBlockTeamMemberDialog(false)}
            onClose={() => setShowBlockTeamMemberDialog(false)}
          />
        )}
        <HeadSection>
          <JaneAvatarWrapper>
            <TeamMemberJaneAvatar
              url={avatarUrls?.origin}
              status={accessStatus?.name}
              size={112}
              pendingSignup={pendingSignup}
            />
          </JaneAvatarWrapper>
          <Flex justifyContent="space-between" width="100%">
            <Flex flexDirection="column" gap="15px" width="100%">
              <Flex justifyContent="space-between">
                <JaneName>{shorthandName}</JaneName>
                {showEdit && (
                  <FeatureGuard NOT type="FEATURE_TYPE_ACCUSHIELD_SYNCING">
                    <JaneMenu
                      disabled={false}
                      setShowDeactivateConfirmModal={setShowBlockTeamMemberDialog}
                      accessStatusName={accessStatus?.name}
                      reactivateTeamMember={reactivateTeamMember}
                    />
                  </FeatureGuard>
                )}
              </Flex>
              <Flex alignItems="center" margin="0 0 0 4px">
                <ColorDot color={dotColor}>•</ColorDot>
                <Text>
                  {accessStatus.name === 'blocked'
                    ? t('team_member:Deactivated')
                    : t(`team_member:${startCase(accessStatus?.name)}`)}
                </Text>
                {pendingSignup && accessStatus?.name === 'approved' && (
                  <PendingSignUpText>
                    ({i18next.t('team_member:PENDING_MOBILE_APP_SIGN_UP')})
                  </PendingSignUpText>
                )}
              </Flex>
              <EllipsisText>{primaryPosition?.details?.name}</EllipsisText>
              {isMobile && (
                <NonAppUserWrapper>
                  <Flex>
                    <BKJBadge variant="Grey"> {t('team_member:Non_App_User')}</BKJBadge>
                    <BKJIconTooltip iconName="Info" placement="bottom">
                      <TooltipWrapper>
                        {i18next.t('team_member:STAFF_WILL_RECEIVE_AUTOMATED_PHONE')}
                      </TooltipWrapper>
                    </BKJIconTooltip>
                  </Flex>
                </NonAppUserWrapper>
              )}
              <div>
                <StarRating rating={averageRating} />
              </div>
            </Flex>
          </Flex>
        </HeadSection>
      </Flex>
      <Flex padding="40px 32px" flexDirection="column" gap="32px">
        <H4>{t('team_member:Edit Profile')}</H4>
        <FieldWrapper>
          <LabelText>{t('team_member:PRIMARY_LOCATION')}</LabelText>
          <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
            {communityName}
          </ValueText>
        </FieldWrapper>
        <Fragment>
          {isMultiLocationSchedulingEnabled && (
            <FieldWrapper>
              <LabelText>{t('team_member:SECONDARY_LOCATIONS_SCHEDULING')}</LabelText>
              {usersCommunities?.filter(secondaryLocationSchedulingFilter)?.length ? (
                usersCommunities
                  .filter(secondaryLocationSchedulingFilter)
                  .map((secondaryLocation) => (
                    <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                      {secondaryLocation.community.name}
                    </ValueText>
                  ))
              ) : (
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>-</ValueText>
              )}
            </FieldWrapper>
          )}
          {(isMultiLocationTieringEnabled || isMultiLocationCallOutEnabled) && (
            <FieldWrapper>
              <LabelText>{t('team_member:SECONDARY_LOCATIONS_CALL_OUT')}</LabelText>
              {usersCommunities?.filter(secondaryLocationCallOutFilter)?.length ? (
                usersCommunities
                  .filter(secondaryLocationCallOutFilter)
                  .map((secondaryLocation) => (
                    <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>
                      {secondaryLocation.community.name}
                    </ValueText>
                  ))
              ) : (
                <ValueText marginLeft={shiftModalGeneralTabValueTextMarginLeft}>-</ValueText>
              )}
            </FieldWrapper>
          )}
        </Fragment>

        <Flex flexDirection="column" gap="32px" padding="0 0 50px" width="100%">
          <Flex flexDirection="column" gap="8px">
            <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
              {t('team_member:PRIMARY_POSITION') + '*'}
            </LabelText>
            <SelectInputPositionByDepartment
              dropdownVariant="Square"
              placeholder={t('team_member:SELECT_POSITION')}
              onChange={handlePrimaryPosition}
              type="Single"
              name="position_id"
              variant="GreyOutlined"
              value={values?.position_id}
              error={fieldErrors?.position_id}
              width="100%"
              key={`${primaryCommunityId}`}
              allocatedToCommunities={[`${primaryCommunityId}`]}
              dropdownProps={{
                dropdownListItemVariant: 'List',
                isSearchable: false,
              }}
            />
          </Flex>
          <Flex flexDirection="column">
            <LabelText style={{ fontWeight: 'normal', fontFamily: 'Poppins Medium' }}>
              {t('team_member:SECONDARY_POSITIONS')}
            </LabelText>
            <BKJPillDropdown
              onChange={handleSecondaryPosition}
              name="selectedSecondaryPositions"
              value={values?.selectedSecondaryPositions}
              key={`${values?.location?.value}`}
              options={secondaryPositionsDropdownItems}
              validations={[]}
              error={fieldErrors.selectedSecondaryPositions}
              placeholder={t('team_member:SELECT_POSITIONS')}
              max={secondaryPositionsDropdownItems?.length || 0}
            />
          </Flex>
        </Flex>
      </Flex>
      <Flex height="100%"></Flex>
      <BKJSideModalFooter>
        <Flex width="100%" justifyContent="end">
          <BKJButton
            variant="PurpleOutlined"
            width="140px"
            onClick={() => {
              setIsDiscardConfirmationOpen(true);
            }}
            type="button"
          >
            {t('team_member:CANCEL')}
          </BKJButton>
          <BKJButton
            variant="PurpleSolid"
            disabled={isDisabled || isSubmitting}
            isLoading={isSubmitting}
            width="140px"
            onClick={onSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
            type="button"
            style={{ marginLeft: '4%' }}
          >
            {t('auth:SAVE')}
          </BKJButton>
        </Flex>
      </BKJSideModalFooter>
      <BKJCenteredDialog
        isOpen={isDiscardConfirmationOpen}
        variant="Danger"
        title={t('create_shifts:DISCARD_CHANGES')}
        data-cy="discard-modal"
        text=""
        cancelLabel={t('auth:CANCEL')}
        actionLabel={t('create_shifts:DISCARD')}
        onAction={() => {
          setIsDiscardConfirmationOpen(false);
          return removePositionModalRoutingService.open({ janeId }, 'JaneDetailsView');
        }}
        onCancel={() => setIsDiscardConfirmationOpen(false)}
        onClose={() => setIsDiscardConfirmationOpen(false)}
      />
    </Fragment>
  );
};

export const EditJaneDefaultFormComponent: FC = () => {
  const { data, isLoading } = useFetchJaneByIdDataContext();

  if (isLoading)
    return (
      <LoadingWrapper>
        <BKJLoadingSpinner variant="Default" />
      </LoadingWrapper>
    );
  return (
    <SelectInputPositionByDepartmentProvider allocatedToCommunities={[data?.primaryCommunity?.id]}>
      <EditJaneViewFormProvider>
        <Component />
      </EditJaneViewFormProvider>
    </SelectInputPositionByDepartmentProvider>
  );
};
export const EditJaneView: FC = () => {
  const [{ janeId }] = useURLParameters<{ janeId?: string }>();
  return (
    <FetchJaneByIdByIdDataProvider janeId={janeId!}>
      <EditJaneDefaultFormComponent />
    </FetchJaneByIdByIdDataProvider>
  );
};
